import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Card,
  CardMedia,
  CardContent,
  SvgIcon,
  styled
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getAllGames, getTextualContent } from '../apiCalls/games/api_v1_games';

import ic_callofduty from '../static/img/gameIcons/ic_callofduty.png';
import ic_valorant from '../static/img/gameIcons/ic_valorant.png';
import ic_pubg from '../static/img/gameIcons/ic_pubg.png';
import ic_fortnite from '../static/img/gameIcons/ic_fortnite.png';
import ic_freefire from '../static/img/gameIcons/ic_freefire.png';
import ic_minecraft from '../static/img/gameIcons/ic_minecraft.png';

const ArrowIcon = () => (
  <SvgIcon sx={{ fontSize: '16px', marginLeft: '8px' }}>
    <svg viewBox="0 0 640 640">
      <path fill="currentColor" d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z"/>
    </svg>
  </SvgIcon>
);

const ScrollableChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  paddingBottom: '8px',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
}));

const StyledChip = styled(Chip)(({ theme, selected }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: '14px',
  fontWeight: '500',
  backgroundColor: selected ? '#FAAE1C' : '#2A2A2A',
  color: selected ? '#000000' : '#FFFFFF',
  border: `1px solid ${selected ? '#FAAE1C' : '#444444'}`,
  borderTop: selected ? '2px solid #FFFFFF' : `1px solid ${selected ? '#FAAE1C' : '#444444'}`,
  borderRadius: '10px',
  padding: '8px 16px',
  height: 'auto',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: selected ? '#FAAE1C' : '#3A3A3A',
  },
  '& .MuiChip-label': {
    padding: 0,
  },
}));

const PreviewCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#252525',
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: '24px',
  border: 'none',
  borderTop: '6px solid #FAAE1C',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease-in-out',
  },
  '&:last-child': {
    marginBottom: '32px',
  },
}));

const GameIconHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: 'rgb(55, 55, 55)',
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
}));

const GameIcon = styled('img')(({ theme }) => ({
  width: '32px',
  height: 'auto',
  borderRadius: '6px',
}));

const ContentSnippet = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: '14px',
  fontWeight: '400',
  color: '#D9D9D9',
  lineHeight: '1.5',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginBottom: '16px',
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: '14px',
  fontWeight: '500',
  color: '#F2BC1A',
  textTransform: 'none',
  padding: '12px 24px',
  minWidth: 'auto',
  backgroundColor: 'rgb(55, 55, 55)',
  border: '1px solid rgb(55, 55, 55)',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  width: '100%',
  '&:hover': {
    backgroundColor: '#F2BC1A',
    color: '#000000',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
  },
}));

function NewsDetails() {
  const theme = useTheme();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const { gameName } = useParams();
  const navigate = useNavigate();
  const gameChipRefs = useRef({});
  
  const [selectedCategories, setSelectedCategories] = useState(['article']);
  const [selectedGame, setSelectedGame] = useState(() => {
    if (gameName) {
      const displayName = gameName.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      if (displayName === 'Call Of Duty') return 'Call of Duty';
      if (displayName === 'Pubg') return 'PUBG';
      return displayName;
    }
    return 'Call of Duty';
  });
  
  const [games, setGames] = useState([]);
  const [gameUuidMap, setGameUuidMap] = useState({});
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { label: 'Articles', value: 'article' },
    { label: 'Latest News', value: 'news' },
    { label: 'Reviews', value: 'review' },
    { label: 'Pros & Cons', value: 'pros_cons' }
  ];

  const gameIcons = {
    'Call of Duty': ic_callofduty,
    'Valorant': ic_valorant,
    'PUBG': ic_pubg,
    'Fortnite': ic_fortnite,
    'Freefire': ic_freefire,
    'Minecraft': ic_minecraft,
  };

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await getAllGames();
        if (response.data.success) {
          const gamesData = response.data.data.games;
          setGames(gamesData.map(game => game.name));
          
          const uuidMap = {};
          gamesData.forEach(game => {
            uuidMap[game.name] = game.uuid;
          });
          setGameUuidMap(uuidMap);
          
          if (!selectedGame || !uuidMap[selectedGame]) {
            setSelectedGame(gamesData[0]?.name || 'Call of Duty');
          }
        }
      } catch (error) {
        console.error('Error fetching games:', error);
        setGames(['Call of Duty', 'Valorant', 'PUBG', 'Fortnite', 'Freefire', 'Minecraft']);
      }
    };
    
    fetchGames();
  }, []);
  
  useEffect(() => {
    const fetchArticles = async () => {
      if (!selectedGame || !gameUuidMap[selectedGame]) {
        return;
      }
      
      setLoading(true);
      try {
        const gameUuid = gameUuidMap[selectedGame];
        const contentTypes = selectedCategories.join(',');
        
        const response = await getTextualContent({
          game_id: gameUuid,
          content_type: contentTypes
        });
        
        if (response.data.success) {
          setArticles(response.data.data || []);
        } else {
          setArticles([]);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [selectedGame, selectedCategories, gameUuidMap]);

  useEffect(() => {
    document.title = `News, Articles, Reviews | Gamez Academy`;
  }, []);

  useEffect(() => {
    if (gameChipRefs.current[selectedGame]) {
      gameChipRefs.current[selectedGame].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedGame]);

  const handleCategoryClick = (categoryValue) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryValue)) {
        return prev.length > 1 ? prev.filter(c => c !== categoryValue) : prev;
      } else {
        return [...prev, categoryValue];
      }
    });
  };

  const handleGameClick = (game) => {
    setSelectedGame(game);
    const formattedGameName = game.toLowerCase().replace(/\s+/g, '-');
    navigate(`/news/${formattedGameName}`, { replace: true });
  };

  return (
    <Box
      sx={{
        width: '100%',
        padding: isDesktop ? '20px' : '16px',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh'
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: '16px',
          fontWeight: '500',
          lineHeight: '133%',
          textAlign: 'left',
          color: '#FFFFFF',
          marginBottom: '8px'
        }}
      >
        News, Articles, Reviews
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: '12px',
          fontWeight: '300',
          color: '#F7F7F7',
          textAlign: 'left',
          marginBottom: '24px'
        }}
      >
        Read Everyday news, Articles & reviews to stay update.
      </Typography>

      <Box sx={{ marginBottom: '8px' }}>
        <ScrollableChipContainer>
          {categories.map((category) => (
            <StyledChip
              key={category.value}
              label={category.label}
              selected={selectedCategories.includes(category.value)}
              onClick={() => handleCategoryClick(category.value)}
              clickable
            />
          ))}
        </ScrollableChipContainer>
      </Box>

      <Box sx={{ marginBottom: '24px' }}>
        <ScrollableChipContainer>
          {games.map((game) => (
            <StyledChip
              key={game}
              ref={(el) => gameChipRefs.current[game] = el}
              label={game}
              selected={selectedGame === game}
              onClick={() => handleGameClick(game)}
              clickable
            />
          ))}
        </ScrollableChipContainer>
      </Box>

      <Box sx={{ marginTop: '32px', paddingBottom: '24px' }}>
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <PreviewCard key={`loading-${index}`}>
              <GameIconHeader>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#F2BC1A',
                    lineHeight: '24px',
                    textTransform: 'capitalize',
                    flex: 1,
                    textAlign: 'center'
                  }}
                >
                  Loading...
                </Typography>
              </GameIconHeader>
            </PreviewCard>
          ))
        ) : articles.length > 0 ? (
          articles.map((article) => (
            <PreviewCard key={article.id}>
              <GameIconHeader>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#F2BC1A',
                    lineHeight: '24px',
                    textTransform: 'capitalize',
                    flex: 1,
                    textAlign: 'center'
                  }}
                >
                  {selectedGame} {article.type}
                </Typography>
                <GameIcon 
                  src={gameIcons[selectedGame]} 
                  alt={selectedGame}
                />
              </GameIconHeader>
              
              <CardContent sx={{ padding: '16px', paddingTop: '12px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#FFFFFF',
                    lineHeight: '1.3',
                    marginBottom: '12px'
                  }}
                >
                  {article.title}
                </Typography>
                
                {article.image && (
                  <CardMedia
                    component="img"
                    image={article.image}
                    alt={article.title}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: isDesktop ? '400px' : '200px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      marginBottom: '12px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                
                <ContentSnippet
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
                
                <ReadMoreButton
                  onClick={() => navigate('/article-view', {
                    state: {
                      article: article,
                      gameName: selectedGame
                    }
                  })}
                >
                  Read more <ArrowIcon />
                </ReadMoreButton>
              </CardContent>
            </PreviewCard>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '16px',
                color: '#888888'
              }}
            >
              No {selectedCategories.map(cat => categories.find(c => c.value === cat)?.label).join(', ').toLowerCase()} found for {selectedGame}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default NewsDetails;